import React from 'react';
import { Link } from 'react-router-dom';
import LegalPage from '../components/LegalPage';
import { SITE } from '../content/site';
import MailLink from '../components/MailLink';

const sections = [
  {
    id: 'agreement',
    title: 'The agreement',
    content: (
      <>
        <p>
          These terms govern your use of any mobile application published by{' '}
          {SITE.name} and of {SITE.domain}. Installing or using an app means you
          accept them. If you do not, uninstall the app — that is the whole exit
          process.
        </p>
        <p>
          Your privacy is covered separately and forms part of this agreement:{' '}
          <Link to="/privacy">privacy policy</Link>.
        </p>
      </>
    ),
  },
  {
    id: 'licence',
    title: 'What you may do',
    content: (
      <>
        <p>
          We grant you a personal, non-exclusive, non-transferable, revocable
          licence to install and use our apps on devices you control, for your
          own use.
        </p>
        <p>You may not:</p>
        <ul>
          <li>
            copy, sell, rent, sublicense or redistribute an app or any part of
            it;
          </li>
          <li>
            reverse-engineer, decompile or modify an app, except where that
            right cannot lawfully be excluded;
          </li>
          <li>
            strip, hide or interfere with ads, purchase flows, or any security
            or licensing mechanism;
          </li>
          <li>
            use an app to break the law, to infringe someone&apos;s rights, or
            to attack or overload our services or anyone else&apos;s.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 'accounts',
    title: 'Accounts',
    content: (
      <p>
        Most of our apps need no account. Where one is offered, you are
        responsible for keeping access to the email or sign-in provider you used,
        and for what happens under your account.{' '}
        <MailLink subject="Account security">Tell us</MailLink> if you think it
        has been compromised. You can close an account at any time —{' '}
        <Link to="/delete-account">see the deletion page</Link>.
      </p>
    ),
  },
  {
    id: 'content',
    title: 'Your content',
    content: (
      <p>
        Anything you create inside an app — notes, saved items, photos you
        import, scores — stays yours. We claim no ownership of it. Where an app
        stores that content only on your device, we have no copy of it and
        cannot restore it if the device is lost.
      </p>
    ),
  },
  {
    id: 'ads-and-purchases',
    title: 'Ads, purchases and refunds',
    content: (
      <>
        <p>
          Free apps are supported by ads. Where a paid tier exists, it is sold
          and billed by Google Play, not by us: the price, the currency, the tax
          and the renewal are handled there.
        </p>
        <p>
          Cancel a subscription in Google Play before its renewal date —
          deleting an account or uninstalling an app does not cancel it. Refunds
          follow Google Play&apos;s policy; where we can help with a refund
          request, write to us and we will.
        </p>
      </>
    ),
  },
  {
    id: 'availability',
    title: 'Availability and changes',
    content: (
      <p>
        Apps are provided as they are. We add, change and remove features, and
        we may discontinue an app entirely. Where an app is retired we will say
        so on its store listing. Nothing here promises uninterrupted service, and
        third-party platforms we depend on — Google Play, device manufacturers,
        network operators — can break things outside our control.
      </p>
    ),
  },
  {
    id: 'disclaimer',
    title: 'No warranty',
    content: (
      <p>
        To the fullest extent the law allows, our apps are provided
        &quot;as is&quot; and &quot;as available&quot;, without warranties of
        any kind, express or implied, including merchantability, fitness for a
        particular purpose and non-infringement. Content inside an app — including
        any informational, educational, astrological, financial or
        health-adjacent material — is for general interest only and is not
        professional advice. Do not make a medical, legal or financial decision
        on the strength of an app of ours.
      </p>
    ),
  },
  {
    id: 'liability',
    title: 'Limitation of liability',
    content: (
      <p>
        To the extent permitted by law, {SITE.name} is not liable for indirect,
        incidental, special or consequential damages, nor for lost data, lost
        profits or lost opportunity, arising from your use of an app. Where
        liability cannot be excluded, it is limited to the greater of the amount
        you paid us for the app in the twelve months before the claim, or
        ₹1,000. Nothing here limits liability for fraud, death or personal
        injury caused by negligence, or anything else that cannot lawfully be
        limited.
      </p>
    ),
  },
  {
    id: 'termination',
    title: 'Termination',
    content: (
      <p>
        You may stop using an app at any time by uninstalling it. We may suspend
        or end your licence if you breach these terms, if it is required to
        comply with the law, or if continuing would damage other users. Sections
        that by their nature survive — liability, disclaimers, governing law —
        continue to apply afterwards.
      </p>
    ),
  },
  {
    id: 'law',
    title: 'Governing law',
    content: (
      <p>
        These terms are governed by the laws of India, and the courts of India
        have exclusive jurisdiction over any dispute. If you are a consumer in
        another country, this does not take away rights your local law gives you
        that cannot be waived.
      </p>
    ),
  },
  {
    id: 'changes',
    title: 'Changes to these terms',
    content: (
      <p>
        We update these terms as the apps change. The date at the top of this
        page moves when they do, and material changes are announced in the app
        or on its store listing before they take effect. Continuing to use an app
        after that date means the new terms apply.{' '}
        <MailLink subject="Terms question">Questions go here</MailLink>.
      </p>
    ),
  },
];

function Terms() {
  return (
    <LegalPage
      title="Terms of Service"
      applies={`Applies to all ${SITE.name} apps and ${SITE.domain}`}
      lede="The rules that come with the apps: what you may do with them, what we promise, and what we do not."
      sections={sections}
    />
  );
}

export default Terms;
